import { INetworkResult } from '@/coreDomainsModule/networkResult';
import { ExportInventoryExcelRequest } from '@/coreInfrastructureModule/axios/generated/inventoryManage';
import { IInventoryRepository } from '@/coreInfrastructureModule/interfaces/IInventoryRepository';
import { IError } from '@/coreInfrastructureModule/interfaces/IError';
import { IYearMonth } from '@/coreDomainsModule/date';
import { OutputFormat, IExportInventoryResultData } from '../domains/entities/InventoryTemplate';
import { FilterContentItem } from '@/corePagesModule/filterModal/filterModal';
import { FilterService } from '@/coreDomainsModule/services/filterService';

/**
 * テンプレートベースのExcelエクスポートを実行するUseCase
 */
export class ExportInventoryExcelUseCase {
  private repository: IInventoryRepository;

  /**
   * コンストラクタ
   * @param repository - 在庫情報のリポジトリ
   */
  constructor(repository: IInventoryRepository) {
    this.repository = repository;
  }

  /**
   * Excelエクスポートを実行する
   * @param masterItems - 出力対象のマスタ項目キーの配列
   * @param outputDataItems - 出力対象のデータ項目キーの配列
   * @param startYearMonth - 開始年月
   * @param endYearMonth - 終了年月
   * @param outputFormat - 出力形式（byMonth or byDataType）
   * @param singleFieldPositions - 単一フィールド項目の挿入位置（任意）
   * @param language - 出力言語（ja or en、デフォルト: ja）
   * @param filterItems - フィルター項目の配列（任意）
   */
  public async execute(
    masterItems: string[],
    outputDataItems: string[],
    startYearMonth: IYearMonth,
    endYearMonth: IYearMonth,
    outputFormat: OutputFormat,
    singleFieldPositions?: { [key: string]: number },
    language?: 'ja' | 'en',
    filterItems?: FilterContentItem[]
  ): Promise<INetworkResult<IExportInventoryResultData, IError>> {
    // フィルター項目をAPI用の型に変換
    const [filterItem, filterItemObject] =
      filterItems && filterItems.length > 0 ? FilterService.convertFilterObjectWithInfo(filterItems) : [undefined, undefined];

    const request: ExportInventoryExcelRequest = {
      masterItems,
      outputDataItems,
      startYearMonth,
      endYearMonth,
      outputFormat,
      singleFieldPositions,
      language: language ?? 'ja',
      filterItem,
      filterItemObject,
    };

    const networkResult = await this.repository.exportExcel(request);
    switch (networkResult.status.type) {
      case 'success':
        return {
          status: {
            type: 'success',
            data: networkResult.status.data.result,
          },
        };
      case 'error':
        return { status: { type: 'error', error: networkResult.status.error } };
    }
  }

  /**
   * S3の署名付きURLからExcelファイルをダウンロードする
   * @param downloadUrl - S3からのダウンロード用署名付きURL
   * @param fileName - ダウンロード用ファイル名（フォールバック用）
   */
  public static downloadExcel(downloadUrl: string, fileName: string): void {
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }
}
